import z from "zod";
import { BetIntentSchema } from "./plain-schemas";
import { EventDbSchema, EventMarketDbSchema } from "./db-schemas";
import { Address, encodeAbiParameters, keccak256, parseAbiParameters, toHex } from "viem";

type BetIntent = z.infer<typeof BetIntentSchema>;
type BetIntentUnsigned = BetIntent extends infer T
	? T extends { initiator_signature: string }
		? Omit<T, "initiator_signature">
		: never
	: never;

function getMultipliersConfigId(bet_intent: BetIntentUnsigned): number {
	if(bet_intent.type === "powerplay") {
		return Number(bet_intent.combo_multipliers_config_onchain_id);
	}
	return Number(bet_intent.flex_combo_multipliers_config_onchain_id);
}

export function preparePlaceBetPicksForSigPayload({
	bet_intent,
}: {
	bet_intent: BetIntentUnsigned
}) {
	const picks_raw = bet_intent.picks.map(pick => ({
		event_market_id: pick.event_market_onchain_id as Address,
		outcome_id: pick.outcome_onchain_id as Address,
	} as const));
	return picks_raw;
}

export function preparePlaceBetLazyCreateEventMarketsForSigPayload({
	used_events,
	used_event_markets,
	bet_intent,
}: {
	used_events: z.infer<typeof EventDbSchema>[],
	used_event_markets: z.infer<typeof EventMarketDbSchema>[],
	bet_intent: BetIntentUnsigned
}) {
	const lazy_create_event_markets_raw = bet_intent.picks.map(pick => {

		// TODO: optimize using maps
		const event_market = used_event_markets.find(n => n.onchain_id === pick.event_market_onchain_id);
		if (!event_market) {
			throw new Error(`Event market not found for onchain id ${pick.event_market_onchain_id}`);
		}

		const event = used_events.find(n => n.id === event_market.event_id);
		if (!event) {
			throw new Error(`Event not found for id ${event_market.event_id}`);
		}

		return {
			event_market_id: pick.event_market_onchain_id as Address,
			min_settlement_ts: Math.floor(event.commence_date.getTime() / 1000),
		} as const;
	});
	return lazy_create_event_markets_raw;
}

export function buildPlaceBetSigPayload({
	bet_intent,
	picks_raw,
	lazy_create_event_markets_raw,
}: {
	bet_intent: BetIntentUnsigned,
	picks_raw: { event_market_id: Address, outcome_id: Address }[],
	lazy_create_event_markets_raw: { event_market_id: Address, min_settlement_ts: number }[],
}) {

	const hash_abi = 'bytes32, uint256, address, address, (bytes12,bytes12)[], (bytes12,uint40)[], uint128, uint8, uint256, uint256, uint8';
	const encoded_message = encodeAbiParameters(
		parseAbiParameters(hash_abi),
		[
			keccak256(toHex("placeBet")),
			BigInt(bet_intent.chain_id),
			bet_intent.contract_address as Address,
			bet_intent.owner_wallet_address as Address,
			picks_raw.map(n => [n.event_market_id, n.outcome_id] as const),
			lazy_create_event_markets_raw.map(n => [n.event_market_id, n.min_settlement_ts] as const),
			BigInt(bet_intent.size_raw),
			getMultipliersConfigId(bet_intent),
			BigInt(bet_intent.deadline_ts),
			BigInt(bet_intent.wallet_nonce),
			Number(bet_intent.token_type),
		]
	);

	const message_hash = keccak256(encoded_message);

	return message_hash;
}
